import { useEffect, useState, type DragEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { isApiError } from '@dorixona/api'
import { NEWS_LEAD_MAX, NEWS_MEDIA_MAX_BYTES, NEWS_TITLE_MAX } from '@dorixona/api/news'
import { formatDayTime, nowMs, parseLocalDateTime, parseMs } from '@dorixona/utils'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { Icon } from '../../ui/Icon'
import { Modal } from '../../ui/Modal'
import { AutoTextarea } from '../../ui/AutoTextarea'
import { DetailHead } from '../../ui/DetailHead'
import { DateTimePicker } from '../../ui/DatePicker'
import { Failed, Loading } from '../../ui/Load'
import { useSession } from '../auth/session'
import { RichText } from '../settings/RichText'
import { IMAGE_ACCEPT } from '../stories/model'
import { useDeleteNews, useNews, useSaveNews, useUploadNewsPhoto, type News } from './api'
import {
  PUBLISH_MODES,
  STATUS_TEXT,
  STATUS_TONE,
  TEXTS,
  draftProblem,
  filled,
  fromDraft,
  newNews,
  newsFailureText,
  newsStatus,
  publishChecklist,
  publishMode,
  toDraft,
  withMode,
  type Lang,
  type NewsDraft,
} from './model'

export interface NewsScreenProps {
  onToast: (message: string) => void
}

/** Пустая новость для `/news/new`: черновик без текста, фото и даты. */
const BLANK = newNews()

const LANGS: [Lang, string][] = [
  ['ru', 'Русский'],
  ['uz', 'Oʻzbekcha'],
]

interface PhotoProps {
  draft: NewsDraft
  canUpdate: boolean
  busy: boolean
  onUpload: (file: File) => void
  onRemove: () => void
}

/** Фото новости 16:9 — на карточке в приложении и над текстом. */
function NewsPhoto({ draft, canUpdate, busy, onUpload, onRemove }: PhotoProps) {
  const [error, setError] = useState('')
  const [depth, setDepth] = useState(0)

  function take(file: File | undefined) {
    if (!file) return
    if (file.size > NEWS_MEDIA_MAX_BYTES) {
      setError('Файл больше 5 МБ')
      return
    }
    setError('')
    onUpload(file)
  }

  const dropZone = canUpdate
    ? {
        onDragEnter: (event: DragEvent) => {
          event.preventDefault()
          setDepth((d) => d + 1)
        },
        onDragOver: (event: DragEvent) => {
          event.preventDefault()
        },
        onDragLeave: () => {
          setDepth((d) => d - 1)
        },
        onDrop: (event: DragEvent) => {
          event.preventDefault()
          setDepth(0)
          if (!busy) take(event.dataTransfer.files[0])
        },
      }
    : {}

  const picker = (
    <input
      type="file"
      className="visually-hidden"
      accept={IMAGE_ACCEPT}
      aria-label={draft.photoUrl ? 'Заменить фото' : 'Загрузить фото'}
      disabled={busy}
      onChange={(e) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        take(file)
      }}
    />
  )

  return (
    <section
      className="ui-card ui-drop-card"
      aria-labelledby="nw-photo"
      aria-busy={busy}
      data-drop={depth > 0 || undefined}
      {...dropZone}
    >
      <div className="ui-card-head">
        <h3 id="nw-photo">Фото</h3>
        {busy ? <span className="aside">Загружаем…</span> : null}
      </div>
      <div className="ui-card-body nw-photo-body">
        {draft.photoUrl ? (
          <div className="nw-photo-img">
            <img src={draft.photoUrl} alt="Фото новости" />
            {canUpdate ? (
              <div className="nw-photo-actions">
                <label className="ui-btn" data-variant="secondary" data-size="sm">
                  <Icon name="i-img" />
                  Заменить
                  {picker}
                </label>
                <button
                  type="button"
                  className="ui-iconbtn"
                  aria-label="Убрать фото"
                  data-tip="Убрать фото"
                  onClick={onRemove}
                >
                  <Icon name="i-trash" />
                </button>
              </div>
            ) : null}
          </div>
        ) : canUpdate ? (
          <label className="ui-drop">
            <Icon name="i-img" />
            <b>Перетащите фото сюда</b>
            <span>
              или <u>выберите файл</u> · JPEG, PNG, WebP до 5 МБ · 16:9, от 1200 px
            </span>
            {picker}
          </label>
        ) : (
          <p className="ui-muted-line">Фото нет</p>
        )}
        {error ? (
          <span className="ui-error" role="alert">
            <Icon name="i-alert" />
            {error}
          </span>
        ) : null}
      </div>
      {depth > 0 ? (
        <div className="ui-drop-over" aria-hidden="true">
          <Icon name="i-img" />
          Отпустите, чтобы поставить фото
        </div>
      ) : null}
    </section>
  )
}

/**
 * Карточка новости: тексты на двух языках, фото и публикация. Всё — черновик
 * до кнопки «Сохранить»; опубликовать — тоже через неё: перед публикацией
 * проверяется, что есть фото, текст и дата. Новая новость (`/news/new`)
 * пишется той же формой и уходит на сервер целиком — черновиком или сразу
 * с публикацией; фото грузится до создания, ему id не нужен.
 */
export function NewsScreen({ onToast }: NewsScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const params = useParams()
  const creating = params.id === undefined
  const id = Number(params.id)

  const news = useNews(id, { enabled: !creating })
  const save = useSaveNews()
  const remove = useDeleteNews()
  const upload = useUploadNewsPhoto()
  const [edits, setEdits] = useState<NewsDraft | null>(null)
  const [lang, setLang] = useState<Lang>('ru')
  // Сброс правок — новый редактор: он не управляемый и сам к исходному не вернётся.
  const [generation, setGeneration] = useState(0)
  const [deleting, setDeleting] = useState(false)
  // Свой флаг, а не upload.isPending: тот меняется не сразу, и второй
  // брошенный файл успел бы уйти, пока первый ещё грузится.
  const [photoBusy, setPhotoBusy] = useState(false)
  // Новая новость открывается пустой: заголовок краснеет, только когда из
  // пустого поля ушли. У правки заголовок есть, краснеет стёртый на ходу.
  const [leftTitle, setLeftTitle] = useState(false)

  useEffect(() => {
    setEdits(null)
  }, [id])

  const loaded = creating ? BLANK : news.data
  const dirty =
    edits !== null &&
    loaded !== undefined &&
    JSON.stringify(edits) !== JSON.stringify(toDraft(loaded))
  useUnsavedWarning(dirty || upload.isPending)

  if (creating && !can('news', 'c')) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-news" />
          <b>Добавлять новости роль не разрешает</b>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/news')
            }}
          >
            К списку новостей
          </button>
        </div>
      </div>
    )
  }
  if (!creating && news.isPending) return <Loading what="новость" />
  if (!creating && news.isError && !(isApiError(news.error) && news.error.code === 'not_found')) {
    return (
      <Failed
        what="новость"
        error={news.error}
        onRetry={() => {
          void news.refetch()
        }}
      />
    )
  }
  if (!loaded) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-news" />
          <b>Новость не найдена</b>
          <p>Возможно, её удалили.</p>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/news')
            }}
          >
            К списку новостей
          </button>
        </div>
      </div>
    )
  }

  const current: News = loaded
  const draft = edits ?? toDraft(current)
  const canUpdate = creating ? can('news', 'c') : can('news', 'u')
  const readOnly = !canUpdate
  const status = newsStatus(current, nowMs())
  const problem = draftProblem(draft)
  const { title, lead, body } = TEXTS[lang]
  const now = nowMs()
  const mode = publishMode(draft, now)
  const titleMissing = draft.titleRu.trim() === '' && (!creating || leftTitle)
  // Кнопка говорит, что произойдёт: опубликуется, встанет в расписание или уйдёт в черновики.
  const saveLabel =
    creating && !draft.published
      ? 'Создать новость'
      : dirty && draft.published && !current.published
        ? mode === 'scheduled'
          ? 'Запланировать'
          : 'Опубликовать'
        : dirty && !draft.published && current.published
          ? 'Снять с публикации'
          : 'Сохранить'

  const edit = (patch: Partial<NewsDraft>) => {
    setEdits({ ...draft, ...patch })
  }

  function uploadPhoto(file: File) {
    setPhotoBusy(true)
    upload.mutate(file, {
      onSuccess: (media) => {
        setEdits((prev) => ({
          ...(prev ?? toDraft(current)),
          photoKey: media.key,
          photoUrl: media.url,
        }))
      },
      onError: (error) => {
        onToast(newsFailureText(error))
      },
      onSettled: () => {
        setPhotoBusy(false)
      },
    })
  }

  function saveAll() {
    save.mutate(fromDraft(current.id, draft), {
      onSuccess: (saved) => {
        setEdits(null)
        setGeneration((g) => g + 1)
        onToast(
          saved.published && !current.published
            ? newsStatus(saved, nowMs()) === 'scheduled'
              ? 'Новость запланирована'
              : 'Новость опубликована'
            : creating
              ? 'Черновик новости создан'
              : 'Изменения сохранены',
        )
        if (creating) void navigate(`/news/${saved.id}`)
      },
      onError: (error) => {
        onToast(newsFailureText(error))
      },
    })
  }

  function removeNews() {
    remove.mutate(current.id, {
      onSuccess: () => {
        onToast('Новость удалена')
        void navigate('/news')
      },
      onError: (error) => {
        setDeleting(false)
        onToast(newsFailureText(error))
      },
    })
  }

  const previewAt = parseLocalDateTime(draft.at)

  return (
    <>
      <DetailHead
        backLabel="Новости"
        onBack={() => {
          void navigate('/news')
        }}
        title={creating ? 'Новая новость' : current.titleRu}
        sub={
          creating ? null : (
            <>
              <span className="ui-status" data-tone={STATUS_TONE[status]}>
                {STATUS_TEXT[status]}
              </span>
              {current.publishedAt ? (
                <>
                  <span className="dot" aria-hidden="true" />
                  <span>{formatDayTime(parseMs(current.publishedAt))}</span>
                </>
              ) : null}
            </>
          )
        }
        actions={
          creating ? null : (
            <>
              {readOnly ? <span className="ui-tag">только просмотр</span> : null}
              {can('news', 'd') ? (
                <button
                  type="button"
                  className="ui-iconbtn ui-danger-icon"
                  aria-label="Удалить новость"
                  data-tip="Удалить новость"
                  data-tip-pos="bottom"
                  data-tip-align="end"
                  onClick={() => {
                    setDeleting(true)
                  }}
                >
                  <Icon name="i-trash" />
                </button>
              ) : null}
            </>
          )
        }
      />

      <div className="ui-detail-grid nw-grid">
        <div className="ui-detail-col">
          <section className="ui-card" aria-labelledby="nw-texts">
            <div className="ui-card-head">
              <h3 id="nw-texts">Текст</h3>
              <div className="ui-seg ui-langs" role="group" aria-label="Язык текстов">
                {LANGS.map(([value, label]) => {
                  const done = filled(draft, value)
                  return (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={lang === value}
                      onClick={() => {
                        setLang(value)
                      }}
                    >
                      {label}
                      <span
                        className="ui-count"
                        data-full={done === 3 || undefined}
                        aria-label={`заполнено ${done} из 3`}
                      >
                        {done}/3
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="ui-card-body ui-form">
              <div className="ui-field">
                <label className="ui-label" htmlFor={`nw-${lang}-title`}>
                  Заголовок
                </label>
                <input
                  id={`nw-${lang}-title`}
                  placeholder={
                    lang === 'uz'
                      ? 'Masalan, Chilonzorda yangi dorixona'
                      : 'Например, Новая аптека на Чиланзаре'
                  }
                  className="ui-input"
                  lang={lang}
                  maxLength={NEWS_TITLE_MAX}
                  disabled={readOnly}
                  aria-invalid={lang === 'ru' && titleMissing ? true : undefined}
                  aria-describedby={lang === 'ru' && titleMissing ? 'nw-title-error' : undefined}
                  value={draft[title] as string}
                  onChange={(e) => {
                    edit({ [title]: e.target.value })
                  }}
                  onBlur={() => {
                    if (lang === 'ru') setLeftTitle(true)
                  }}
                />
                {lang === 'ru' && titleMissing ? (
                  <span className="ui-error" id="nw-title-error">
                    <Icon name="i-alert" />
                    Без заголовка новость не сохранить
                  </span>
                ) : null}
              </div>
              <div className="ui-field">
                <label className="ui-label" htmlFor={`nw-${lang}-lead`}>
                  Анонс
                </label>
                <AutoTextarea
                  id={`nw-${lang}-lead`}
                  placeholder={
                    lang === 'uz'
                      ? 'Asosiy sahifadagi kartochka uchun qisqa matn'
                      : 'Коротко — для карточки на главной'
                  }
                  lang={lang}
                  maxLength={NEWS_LEAD_MAX}
                  disabled={readOnly}
                  aria-describedby={`nw-${lang}-lead-hint`}
                  value={draft[lead] as string}
                  onChange={(e) => {
                    edit({ [lead]: e.target.value })
                  }}
                />
                <span className="ui-hint" id={`nw-${lang}-lead-hint`}>
                  Одна-две фразы для карточки на главной · {(draft[lead] as string).length}/
                  {NEWS_LEAD_MAX}
                </span>
              </div>
              <div className="ui-field">
                <span className="ui-label" id={`nw-${lang}-body-label`}>
                  Текст новости
                </span>
                <RichText
                  key={`${lang}-${generation}`}
                  html={draft[body] as string}
                  labelledBy={`nw-${lang}-body-label`}
                  lang={lang}
                  readOnly={readOnly}
                  invalid={false}
                  onChange={(html) => {
                    setEdits((prev) => ({ ...(prev ?? toDraft(current)), [body]: html }))
                  }}
                />
              </div>
              {lang === 'uz' ? (
                <span className="ui-hint">
                  Чего нет на узбекском, приложение покажет на русском
                </span>
              ) : null}
            </div>
          </section>
        </div>

        <div className="ui-detail-col">
          <section className="ui-card" aria-labelledby="nw-publish">
            <div className="ui-card-head">
              <h3 id="nw-publish">Публикация</h3>
            </div>
            <div className="ui-card-body ui-form">
              <div className="ui-seg nw-modes" role="group" aria-label="Как выйдет новость">
                {PUBLISH_MODES.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={mode === value}
                    disabled={readOnly}
                    onClick={() => {
                      edit(withMode(draft, value, nowMs()))
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {mode === 'draft' ? (
                <span className="ui-hint">Покупатель не видит черновик</span>
              ) : (
                <>
                  <div className="ui-field">
                    <label className="ui-label" htmlFor="nw-at">
                      {mode === 'scheduled' ? 'Выйдет' : 'Дата на карточке'}
                    </label>
                    <DateTimePicker
                      id="nw-at"
                      label={mode === 'scheduled' ? 'Когда выйдет' : 'Дата на карточке'}
                      disabled={readOnly}
                      describedBy="nw-at-hint"
                      invalid={draft.at === ''}
                      value={draft.at}
                      onChange={(at) => {
                        edit({ at })
                      }}
                    />
                    <span className="ui-hint" id="nw-at-hint">
                      {mode === 'scheduled'
                        ? 'Новость появится в приложении сама в это время'
                        : 'Её видит покупатель; в будущем — новость выйдет по расписанию'}
                    </span>
                  </div>
                  <ul className="nw-checklist" aria-label="Что нужно для публикации">
                    {publishChecklist(draft).map((item) => (
                      <li key={item.label} data-ok={item.ok || undefined}>
                        <Icon name={item.ok ? 'i-check' : 'i-x'} size={16} />
                        {item.label}
                        <span className="visually-hidden">{item.ok ? ' — есть' : ' — нет'}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </section>

          <NewsPhoto
            draft={draft}
            canUpdate={canUpdate}
            busy={photoBusy}
            onUpload={uploadPhoto}
            onRemove={() => {
              edit({ photoKey: '', photoUrl: '' })
            }}
          />

          <section className="ui-card" aria-labelledby="nw-preview">
            <div className="ui-card-head">
              <h3 id="nw-preview">Как в приложении</h3>
            </div>
            <div className="ui-card-body">
              <article className="nw-preview" aria-label="Карточка новости на главной">
                <div className="nw-preview-img">
                  {draft.photoUrl ? <img src={draft.photoUrl} alt="" /> : <Icon name="i-img" />}
                </div>
                <div className="nw-preview-txt">
                  <span className="nw-preview-date">
                    {previewAt === null ? 'Дата не выбрана' : formatDayTime(previewAt)}
                  </span>
                  <b>{draft.titleRu || 'Заголовок'}</b>
                  {draft.leadRu ? <p>{draft.leadRu}</p> : null}
                </div>
              </article>
            </div>
          </section>
        </div>
      </div>

      {canUpdate ? (
        <div className="ui-savebar" data-dirty={dirty || undefined}>
          <span className="state" role="status">
            {/* Новая сразу говорит, чего не хватает; правка — после первой правки. */}
            {(creating || dirty) && problem
              ? problem
              : dirty
                ? 'Есть несохранённые изменения'
                : creating
                  ? 'Новость ещё не создана'
                  : 'Все изменения сохранены'}
          </span>
          {creating && !dirty ? (
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                void navigate('/news')
              }}
            >
              К списку
            </button>
          ) : null}
          {dirty ? (
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              disabled={save.isPending}
              onClick={() => {
                setEdits(null)
                setGeneration((g) => g + 1)
              }}
            >
              Отменить
            </button>
          ) : null}
          <button
            type="button"
            className="ui-btn"
            disabled={!dirty || problem !== '' || photoBusy || save.isPending}
            aria-busy={save.isPending}
            onClick={saveAll}
          >
            {saveLabel}
          </button>
        </div>
      ) : null}

      {deleting ? (
        <Modal
          title="Удалить новость?"
          onClose={() => {
            setDeleting(false)
          }}
        >
          <p>«{current.titleRu}» пропадёт из админки и из приложения. Вернуть её будет нельзя.</p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setDeleting(false)
              }}
            >
              Не удалять
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={remove.isPending}
              aria-busy={remove.isPending}
              onClick={removeNews}
            >
              Удалить
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}
